import React, { useState, useEffect } from 'react';
import { normalizeUser } from '@/services/supabase';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useDevMode } from '../../utils/environment';
import {
  UserGroupIcon,
  CalendarIcon,
  GiftIcon,
  ChartBarIcon,
  HandRaisedIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CurrencyDollarIcon} from '@heroicons/react/24/outline';
import { DonationsService, type DonationStatistics } from '@/services/donations.service';

// Define types
interface DonationItem {
  id: string;
  business_name: string;
  item_name: string;
  quantity: number;
  date: string;
  status: 'pending' | 'approved' | 'received' | 'cancelled';
}

interface VolunteerApplication {
  id: string;
  name: string;
  email: string;
  phone: string;
  date: string;
  status: 'pending' | 'approved' | 'rejected';
}

interface AvailableDonation {
  id: string;
  business_name: string;
  product_name: string;
  quantity: number;
  expiry_date?: string;
  days_left?: number;
  value?: number;
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  change?: {
    value: number;
    isPositive: boolean;
  };
  color: string;
  iconColor: string;
}

// Reusable components
const StatCard: React.FC<StatCardProps> = ({ title, value, icon, change, color, iconColor }) => (
  <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
    <div className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
          <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
          
          {change && (
            <div className="mt-2 flex items-center">
              {change.isPositive ? (
                <ArrowUpIcon className="h-4 w-4 text-green-500 mr-1" />
              ) : (
                <ArrowDownIcon className="h-4 w-4 text-red-500 mr-1" />
              )}
              <span className={`text-sm ${change.isPositive ? 'text-green-500' : 'text-red-500'}`}>
                {change.value}% from last month
              </span>
            </div>
          )}
        </div>
        
        <div className={`p-3 ${color} rounded-lg`}>
          <div className={`${iconColor}`}>
            {icon}
          </div>
        </div>
      </div>
    </div>
    
    <div className={`h-1 w-full ${color}`}></div>
  </div>
);

// Main component
const CharityDashboard: React.FC = () => {
  const isDevEnvironment = useDevMode();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dashboard data
  const [donationStats, setDonationStats] = useState<DonationStatistics>({
    totalReceived: 0,
    totalValue: 0,
    growth: 0,
    monetaryTotal: 0,
    monetaryGrowth: 0
  });
  
  const [volunteerStats, setVolunteerStats] = useState({
    total: 0,
    active: 0,
    growth: 12.5 // Default value, should be calculated from actual data
  });
  
  const [eventStats, setEventStats] = useState({
    total: 0,
    upcoming: 0,
    growth: -4.3 // Default value, should be calculated from actual data
  });
  
  const [impactStats, setImpactStats] = useState({
    peopleHelped: 0,
    growthRate: 15.7 // Default value, should be calculated from actual data
  });
  
  const [recentDonations, setRecentDonations] = useState<DonationItem[]>([]);
  const [pendingVolunteers, setPendingVolunteers] = useState<VolunteerApplication[]>([]);
  const [availableDonations, setAvailableDonations] = useState<AvailableDonation[]>([]);

  useEffect(() => {
    fetchDashboardData();
  }, []); // Update when component mounts

  const fetchDashboardData = async () => {
    // Check if we're in dev mode
    const isDevMode = () => {
      const devMode = localStorage.getItem('devMode');
      return devMode ? JSON.parse(devMode).active : false;
    };
    
    // Get current user directly from Supabase using the centralized client
    const { createClient } = await import('../../services/supabase');
    const supabase = createClient();
    const { data: { session } } = await (await supabase).auth.getSession();
    
    // Check if we have a valid session
    if (!session?.user && !isDevMode()) {
      console.error('No valid Supabase session found');
      setIsLoading(false);
      return;
    }
    
    // Use normalizeUser to create a consistent user object
    console.debug('Using normalizeUser in CharityDashboard fetchDashboardData');
    const normalizedUser = session?.user ? normalizeUser(session.user) : null;
    
    // Get user role and business_id from normalized user
    const userRole = normalizedUser?.role || 'charity';
    const businessId = normalizedUser?.business_id || null;
    
    // Keep original implementation for backward compatibility
    const isSuperAdmin = userRole === 'super_admin';
    
    // If user is not a charity and not a super admin, and not in dev mode, exit
    if (userRole !== 'charity' && !isSuperAdmin && !isDevMode()) {
      console.error('User is not a charity or super admin');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // For super admin without a specific business_id, we could either:
      // 1. Show aggregated stats across all charities
      // 2. Allow them to select a charity to view
      // 3. Show a message that they need to select a charity
      
      // For now, we'll just proceed with the user's business_id if available
      
      if (businessId) {
        try {
          // Use the DonationsService to get real donation statistics
          const donationStats = await DonationsService.getDonationStatistics(businessId);
          setDonationStats(donationStats);
        } catch (error) {
          console.error('Error fetching donation statistics:', error);
          toast.error('Failed to load donation statistics');
        }
      }
      
      // Fetch volunteer statistics
      // const volunteerStatsResponse = await api.get(`/api/charity/${user.business_id}/volunteer-stats`);
      // setVolunteerStats(volunteerStatsResponse.data);
      
      // Fetch event statistics
      // const eventStatsResponse = await api.get(`/api/business/${user.business_id}/event-stats`);
      // setEventStats(eventStatsResponse.data);
      
      // Fetch impact statistics
      // const impactStatsResponse = await api.get(`/api/business/${user.business_id}/impact-stats`);
      // setImpactStats(impactStatsResponse.data);
      
      // Keep mock data for other stats until proper services are implemented
      // In a real app, these would be fetched from dedicated event and volunteer services
      setEventStats({
        total: 24,
        upcoming: 8,
        growth: -4.3
      });
      
      setVolunteerStats({
        total: 53,
        active: 41,
        growth: 12.5
      });
      
      setImpactStats({
        peopleHelped: 1250,
        growthRate: 15.7
      });
      
      // Fetch recent food donations and transform them to DonationItem format
      try {
        if (businessId) {
          const foodDonations = await DonationsService.getFoodDonations(businessId);
          const recentDonations: DonationItem[] = foodDonations.slice(0, 3).map(donation => ({
            id: donation.id,
            business_name: donation.business_name,
            item_name: donation.items.length > 0 ? donation.items[0].name : 'Multiple items',
            quantity: donation.items.reduce((sum, item) => sum + item.quantity, 0),
            date: DonationsService.formatDate(donation.date),
            status: donation.status === 'pending' ? 'pending' : 
                   donation.status === 'accepted' ? 'approved' : 
                   donation.status === 'picked_up' ? 'received' : 'cancelled'
          }));
          
          setRecentDonations(recentDonations);
        } else if (isSuperAdmin) {
          // For super admin without a specific charity, we could show a sample or aggregated view
          // This is just a placeholder - in a real implementation, you might fetch data for all charities
          // or show a message prompting the admin to select a specific charity
          setRecentDonations([]);
          toast('Super admin view: Select a specific charity to see detailed donations');
        }
      } catch (error) {
        console.error('Error fetching recent donations:', error);
        setRecentDonations([]);
      }

      // Fetch pending volunteer applications
      // const pendingVolunteersResponse = await api.get(`/api/charity/${user.business_id}/pending-volunteers`);
      // setPendingVolunteers(pendingVolunteersResponse.data);
      
      // Mock data for pending volunteers - in a real app, would be fetched from a volunteer service
      // This can be replaced later when a proper volunteer service is implemented
      const mockPendingVolunteers: VolunteerApplication[] = [
        { 
          id: '2001', 
          name: 'John Smith', 
          email: 'john.smith@example.com', 
          phone: '(555) 123-4567', 
          date: '2025-05-14', 
          status: 'pending' 
        },
        { 
          id: '2002', 
          name: 'Maria Rodriguez', 
          email: 'maria.r@example.com', 
          phone: '(555) 987-6543', 
          date: '2025-05-13', 
          status: 'pending' 
        },
      ];
      
      setPendingVolunteers(mockPendingVolunteers);
      
      // Fetch available donations
      // const availableDonationsResponse = await api.get(`/api/donations/available`);
      // setAvailableDonations(availableDonationsResponse.data);
      
      // For available donations, we could fetch recently ended products that could be donated
      // In a real app, these would be from a product service for available items
      // This can be replaced when the product service is properly integrated
      const mockAvailableDonations: AvailableDonation[] = [
        { 
          id: '3001', 
          business_name: 'Metro Supermarket', 
          product_name: 'Mixed Fruits', 
          quantity: 20, 
          expiry_date: '2025-05-20', 
          days_left: 5,
          value: 120
        },
        { 
          id: '3002', 
          business_name: 'Farm Fresh Foods', 
          product_name: 'Organic Vegetables', 
          quantity: 15, 
          expiry_date: '2025-05-18', 
          days_left: 3,
          value: 85
        },
        { 
          id: '3003', 
          business_name: 'Bread Basket', 
          product_name: 'Assorted Breads', 
          quantity: 30, 
          expiry_date: '2025-05-17', 
          days_left: 2,
          value: 150
        },
      ];
      
      setAvailableDonations(mockAvailableDonations);
      
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard information');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproveVolunteer = async (id: string) => {
    try {
      // In a real app, this would call an API
      // await api.put(`/api/volunteers/${id}/approve`);
      
      // Update local state to reflect the change
      setPendingVolunteers(prev => 
        prev.map(vol => vol.id === id ? {...vol, status: 'approved'} : vol)
      );
      toast.success('Volunteer approved successfully!');
    } catch (err) {
      console.error('Error approving volunteer:', err);
      toast.error('Failed to approve volunteer');
    }
  };

  const handleRejectVolunteer = async (id: string) => {
    try {
      // In a real app, this would call an API
      // await api.put(`/api/volunteers/${id}/reject`);
      
      // Update local state to reflect the change
      setPendingVolunteers(prev => 
        prev.map(vol => vol.id === id ? {...vol, status: 'rejected'} : vol)
      );
      toast.success('Volunteer application rejected');
    } catch (err) {
      console.error('Error rejecting volunteer:', err);
      toast.error('Failed to reject volunteer application');
    }
  };

  const handleRequestDonation = async (_id: string) => {
    try {
      // In a real app, this would call an API
      // await api.post(`/api/donations/request`, { product_id: id, charity_id: user.business_id });
      
      // For now, just show a success toast
      toast.success('Donation request sent successfully!');
    } catch (err) {
      console.error('Error requesting donation:', err);
      toast.error('Failed to request donation');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
            {error}
          </div>
        )}

        {/* Stats Cards */}
        <div className="flex flex-wrap -mx-4">
          <div className="w-full sm:w-1/2 lg:w-1/4 px-4 mb-6">
            <StatCard
              title="Total Food Donations"
              value={`${donationStats.totalReceived}`}
              icon={<GiftIcon className="h-6 w-6 text-white" />}
              change={{ value: donationStats.growth, isPositive: donationStats.growth > 0 }}
              color="bg-teal-100"
              iconColor="text-teal-600"
            />
          </div>
          
          <div className="w-full sm:w-1/2 lg:w-1/4 px-4 mb-6">
            <StatCard
              title="Food Donation Value"
              value={DonationsService.formatCurrency(donationStats.totalValue)}
              icon={<ChartBarIcon className="h-6 w-6 text-white" />}
              change={{ value: donationStats.growth, isPositive: donationStats.growth > 0 }}
              color="bg-blue-100"
              iconColor="text-blue-600"
            />
          </div>
          <div className="w-full sm:w-1/2 lg:w-1/4 px-4 mb-6">
            <StatCard
              title="Monetary Donations"
              value={DonationsService.formatCurrency(donationStats.monetaryTotal)}
              icon={<CurrencyDollarIcon className="h-6 w-6 text-white" />}
              change={{ value: donationStats.monetaryGrowth, isPositive: donationStats.monetaryGrowth > 0 }}
              color="bg-pink-100"
              iconColor="text-pink-600"
            />
          </div>
          <div className="w-full sm:w-1/2 lg:w-1/4 px-4 mb-6">
            <StatCard
              title="Active Volunteers"
              value={volunteerStats.active}
              icon={<UserGroupIcon className="h-8 w-8" />}
              change={{ value: volunteerStats.growth, isPositive: volunteerStats.growth > 0 }}
              color="bg-green-50"
              iconColor="text-green-500"
            />
          </div>
          <div className="w-full sm:w-1/2 lg:w-1/4 px-4 mb-6">
            <StatCard
              title="Upcoming Events"
              value={eventStats.upcoming}
              icon={<CalendarIcon className="h-8 w-8" />}
              change={{ value: eventStats.growth, isPositive: eventStats.growth > 0 }}
              color="bg-purple-50"
              iconColor="text-purple-500"
            />
          </div>
          <div className="w-full sm:w-1/2 lg:w-1/4 px-4 mb-6">
            <StatCard
              title="People Helped"
              value={impactStats.peopleHelped}
              icon={<HandRaisedIcon className="h-8 w-8" />}
              change={{ value: impactStats.growthRate, isPositive: impactStats.growthRate > 0 }}
              color="bg-pink-50"
              iconColor="text-pink-500"
            />
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-md p-3 w-full">
          <h2 className="text-lg font-medium text-navy-blue mb-2">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 w-full">
            <Link
              to={isDevEnvironment ? "/dev/charity/events/create" : "/charity/events/create"}
              className="flex flex-col items-center justify-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
            >
              <CalendarIcon className="h-7 w-7 text-purple mb-1.5" />
              <span className="text-sm font-medium text-gray-900">Create Event</span>
            </Link>
            <Link
              to={isDevEnvironment ? "/dev/charity/donations/request" : "/charity/donations/request"}
              className="flex flex-col items-center justify-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
            >
              <GiftIcon className="h-7 w-7 text-blue-500 mb-1.5" />
              <span className="text-sm font-medium text-gray-900">Request Donation</span>
            </Link>
            <Link
              to={isDevEnvironment ? "/dev/charity/volunteers" : "/charity/volunteers"}
              className="flex flex-col items-center justify-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
            >
              <UserGroupIcon className="h-7 w-7 text-green-500 mb-1.5" />
              <span className="text-sm font-medium text-gray-900">Volunteers</span>
            </Link>
            <Link
              to={isDevEnvironment ? "/dev/charity/statistics/analytics" : "/statistics/charity/analytics"}
              className="flex flex-col items-center justify-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
            >
              <ChartBarIcon className="h-7 w-7 text-pink-500 mb-1.5" />
              <span className="text-sm font-medium text-gray-900">View Analytics</span>
            </Link>
          </div>
        </div>
        
        {/* Main content sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Recent Donations */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2 className="text-lg font-medium text-navy-blue">Recent Donations</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentDonations.map((donation) => {
                    const statusColors = {
                      pending: 'bg-yellow-100 text-yellow-800',
                      approved: 'bg-blue-100 text-blue-800',
                      received: 'bg-green-100 text-green-800',
                      cancelled: 'bg-red-100 text-red-800'
                    };
                    
                    return (
                      <tr key={donation.id}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {donation.business_name}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {donation.item_name}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {donation.quantity}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {donation.date}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors[donation.status]}`}>
                            {donation.status.charAt(0).toUpperCase() + donation.status.slice(1)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-gray-200">
              <Link
                to={isDevEnvironment ? "/dev/charity/donations" : "/charity/donations"}
                className="text-sm font-medium text-blue-500 hover:text-blue-700"
              >
                View all donations
              </Link>
            </div>
          </div>

          {/* Volunteer Applications */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2 className="text-lg font-medium text-navy-blue">Pending Volunteer Applications</h2>
            </div>
            <div className="overflow-x-auto">
              {pendingVolunteers.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {pendingVolunteers.map((volunteer) => (
                      <tr key={volunteer.id}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {volunteer.name}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          <div>{volunteer.email}</div>
                          <div>{volunteer.phone}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {volunteer.date}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleApproveVolunteer(volunteer.id)}
                            className="text-green-600 hover:text-green-900 mr-3"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleRejectVolunteer(volunteer.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Reject
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  No pending volunteer applications
                </div>
              )}
            </div>
            <div className="px-4 py-3 border-t border-gray-200">
              <Link
                to={isDevEnvironment ? "/dev/charity/volunteers" : "/charity/volunteers"}
                className="text-sm font-medium text-blue-500 hover:text-blue-700"
              >
                Manage volunteers
              </Link>
            </div>
          </div>
        </div>
        
        {/* Available Donations */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200">
            <h2 className="text-lg font-medium text-navy-blue">Available Donations from Businesses</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {availableDonations.map((product) => {
                  const isExpiring = product.days_left && product.days_left < 30;
                  
                  return (
                    <tr key={product.id}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {product.business_name}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {product.product_name}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {product.quantity}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {product.expiry_date ? (
                          <span className={isExpiring ? 'text-red-600 font-medium' : ''}>
                            {product.expiry_date} 
                            {product.days_left && (
                              <span className="ml-1">({product.days_left} days)</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-medium">
                        ${product.value ? product.value.toLocaleString() : 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleRequestDonation(product.id)}
                          className="text-blue-600 hover:text-blue-900 bg-blue-50 px-2 py-1 rounded"
                        >
                          Request
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-gray-200">
            <Link
              to={isDevEnvironment ? "/dev/charity/donations/request" : "/charity/donations/request"}
              className="text-sm font-medium text-blue-500 hover:text-blue-700"
            >
              View all available donations
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CharityDashboard;
